<template>
  <div class="flex flex-col" :class="props.size">
    <slot name="label">
      <label :for="props.name" :class="props.classes">{{ label }}</label>
    </slot>
    <slot>
      <InputGroup>
        <AutoComplete
          v-on:keydown.enter.prevent
          :name="props.name"
          :id="props.name"
          v-model="value"
          class="w-full"
          input-class="p-2! w-full h-10"
          forceSelection
          :optionLabel="getEntityDisplay"
          :suggestions="filteredLookupItems"
          :disabled
          @complete="search"
        />
        <IconButton class="w-12" icon="search" outlined @click="toggleEntityList(true)"></IconButton>
        <IconButton class="w-12" icon="plus" outlined @click="toggleMaintainEntity('Add')"></IconButton>
      </InputGroup>
      <small v-if="errorMessage" :id="props.name + '-help'" class="text-danger-500">{{ errorMessage }}</small>
    </slot>
  </div>

  <TSDialog
    v-if="maintainEntityMode"
    v-model:visible="displayMaintainEntity"
    :label="maintainEntityMode + ' Entity'"
    icon="user-plus"
    v-on:after-hide="toggleMaintainEntity(undefined)"
    size="w-[1000px]"
  >
    <template #content>
      <MaintainEntity :mode="maintainEntityMode" :entity="undefined" @submitted="entityCreated" />
    </template>
  </TSDialog>
  <TSDialog v-model:visible="displayEntityList" label="Entity Lookup" icon="user" size="w-[1000px]">
    <template #content>
      <EntityLookupListView :lookup-items="lookupItems" @entity-selected="entitySelected"></EntityLookupListView>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete";
import MaintainEntity from "@/components/entity/MaintainEntity.vue";
import type { Entity } from "@/types/Entity";
import { useField } from "vee-validate";
import { getEntityDisplay } from "@/services/Utility";

const lookupItems = defineModel<Entity[]>({ required: true });

const filteredLookupItems = ref<Entity[]>();

interface Props {
  name: string;
  value?: string;
  label: string;
  size?: string;
  classes?: string;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  size: "w-full",
  classes: "font-bold",
  disabled: false,
});

const { value, errorMessage } = useField<Entity | String>(() => {
  return props.name;
});

const maintainEntityMode = ref<"Add" | "Edit" | "Copy">();
const displayMaintainEntity = ref(false);
const entityListActive = ref(false);
const displayEntityList = ref(false);

const toggleMaintainEntity = (value: "Add" | "Edit" | "Copy" | undefined): void => {
  maintainEntityMode.value = value;
  displayMaintainEntity.value = value != null;
};

const entityCreated = (newEntity: Entity | null): void => {
  if (newEntity == null) return;
  lookupItems.value.push(newEntity);
  value.value = newEntity;
  toggleMaintainEntity(undefined);
};

const toggleEntityList = (value: boolean): void => {
  entityListActive.value = value;
  displayEntityList.value = value;
};

const search = (event: AutoCompleteCompleteEvent): void => {
  setTimeout(() => {
    if (!event.query.trim().length) {
      filteredLookupItems.value = [...lookupItems.value];
    } else {
      const searchTerm = event.query.toLowerCase();
      filteredLookupItems.value = lookupItems.value.filter((entity: Entity) => {
        const displayMatch = getEntityDisplay(entity).toLowerCase().startsWith(searchTerm);
        const firstNameMatch = entity.firstName.toLowerCase().startsWith(searchTerm);
        const lastNameMatch = entity.lastName.toLowerCase().startsWith(searchTerm);
        return displayMatch || firstNameMatch || lastNameMatch;
      });
    }
  }, 250);
};

const entitySelected = (entity: Entity): void => {
  toggleEntityList(false);
  value.value = entity;
};
</script>
